/**
 * Lexicon — New Transcript
 *
 * Form for uploading and analyzing a new meeting transcript.
 * Supports paste or file upload. Detects speaker format automatically.
 *
 * Route: /prioritization/lexicon/new
 */

import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Upload, Wand2, ChevronDown, ChevronUp,
  AlertCircle, CheckCircle2, Loader2, Info,
} from 'lucide-react';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { createAndAnalyzeTranscript, detectSpeakers } from '../../../lib/lexiconService';
import { useAiCapability } from '../../../lib/ai/useAiCapability';

// ── Format guide ──────────────────────────────────────────────────────────────

const FORMAT_EXAMPLES = [
  {
    label: 'Name: Text',
    example: 'Jordan: We should prioritize the API work.\nTaylor: Agreed, I can own that by Friday.',
  },
  {
    label: '[Full Name]',
    example: '[Jordan Mercer]\nWe should prioritize the API work.\n\n[Taylor Chen]\nAgreed.',
  },
  {
    label: 'Name (timestamp):',
    example: 'Jordan (10:24): We should prioritize the API work.\nTaylor (10:25): Agreed.',
  },
];

function FormatGuide() {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-[var(--rule)] overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-[var(--surface)] hover:bg-[var(--hover-tint)] transition-colors"
      >
        <span className="flex items-center gap-2 text-sm text-[var(--text-sec)]">
          <Info className="w-4 h-4 text-[var(--text-ter)]" />
          Supported transcript formats
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-[var(--text-ter)]" /> : <ChevronDown className="w-4 h-4 text-[var(--text-ter)]" />}
      </button>
      {open && (
        <div className="px-4 py-4 bg-[var(--bg-raised)] space-y-4">
          <p className="text-xs text-[var(--text-ter)]">
            Speaker attribution is required for per-person analysis. Any of these formats work:
          </p>
          {FORMAT_EXAMPLES.map(ex => (
            <div key={ex.label}>
              <p className="text-xs font-medium text-[var(--text-sec)] mb-1">{ex.label}</p>
              <pre className="text-xs bg-[var(--surface)] border border-[var(--rule)] rounded p-2 whitespace-pre-wrap font-mono text-[var(--text-sec)]">
                {ex.example}
              </pre>
            </div>
          ))}
          <p className="text-xs text-[var(--text-ter)]">
            Transcripts from Zoom, Otter.ai, Fireflies, and most AI meeting tools are detected
            automatically. Remove any personal identifying information you don't need before uploading.
          </p>
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

type Stage = 'idle' | 'analyzing' | 'done' | 'error';

export default function LexiconTranscriptNew() {
  const navigate = useNavigate();
  const { currentWorkspace } = useTenantStore();
  const { userId } = useUserStore();
  const workspaceId = currentWorkspace?.id ?? '';
  const lexiconAiEnabled = useAiCapability('lexicon_analysis');

  const [title, setTitle] = useState('');
  const [meetingDate, setMeetingDate] = useState('');
  const [content, setContent] = useState('');
  const [detectedSpeakers, setDetectedSpeakers] = useState<string[]>([]);
  const [stage, setStage] = useState<Stage>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleContentChange = useCallback((value: string) => {
    setContent(value);
    // Live speaker detection
    if (value.trim().length > 50) {
      const found = detectSpeakers(value);
      setDetectedSpeakers(found);
    } else {
      setDetectedSpeakers([]);
    }
  }, []);

  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const text = ev.target?.result as string;
      handleContentChange(text);
      if (!title) setTitle(file.name.replace(/\.[^.]+$/, ''));
    };
    reader.readAsText(file);
  }, [title, handleContentChange]);

  const canSubmit = title.trim().length > 0 && content.trim().length > 50 && stage === 'idle' && lexiconAiEnabled;

  const handleSubmit = useCallback(async () => {
    if (!canSubmit || !workspaceId) return;

    setStage('analyzing');
    setErrorMsg(null);

    const { data, error } = await createAndAnalyzeTranscript({
      workspaceId,
      title: title.trim(),
      meetingDate: meetingDate || null,
      rawContent: content,
      createdBy: userId ?? undefined,
    });

    if (error || !data) {
      setStage('error');
      setErrorMsg(error ?? 'Analysis failed. Please try again.');
      return;
    }

    setStage('done');
    // Navigate to the first speaker's profile after a short delay
    setTimeout(() => {
      const firstSpeaker = data.analyses[0]?.speakerName;
      if (firstSpeaker) {
        navigate(`/prioritization/lexicon/profile/${encodeURIComponent(firstSpeaker)}`);
      } else {
        navigate('/prioritization/lexicon');
      }
    }, 1500);
  }, [canSubmit, workspaceId, title, meetingDate, content, userId, navigate]);

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-2xl mx-auto px-6 py-6">
        {/* Back nav */}
        <button
          type="button"
          onClick={() => navigate('/prioritization/lexicon')}
          className="flex items-center gap-1.5 text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)] mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Lexicon
        </button>

        <div className="mb-6">
          <h1 className="text-xl font-semibold text-[var(--text-pri)]">Add transcript</h1>
          <p className="text-sm text-[var(--text-ter)] mt-1">
            Paste or upload a meeting transcript. Lexicon will identify speakers and build
            NLP meta-model profiles for each participant.
          </p>
        </div>

        <div className="space-y-5">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1.5" htmlFor="title">
              Meeting title <span className="text-[var(--coral)]">*</span>
            </label>
            <input
              id="title"
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="e.g. Q3 Planning Kickoff, Sprint Review 2026-06-15"
              className="w-full border border-[var(--rule)] px-3 py-2 text-sm text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 transition-colors"
            />
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1.5" htmlFor="date">
              Meeting date <span className="text-[var(--text-ter)] font-normal">(optional)</span>
            </label>
            <input
              id="date"
              type="date"
              value={meetingDate}
              onChange={e => setMeetingDate(e.target.value)}
              className="border border-[var(--rule)] px-3 py-2 text-sm text-[var(--text-pri)] focus:outline-none focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 transition-colors"
            />
          </div>

          {/* Transcript content */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-medium text-[var(--text-sec)]" htmlFor="content">
                Transcript <span className="text-[var(--coral)]">*</span>
              </label>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-1 text-xs text-[var(--text-ter)] hover:text-[var(--text-ter)] transition-colors"
              >
                <Upload className="w-3.5 h-3.5" /> Upload .txt file
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".txt,.md,.text"
                className="hidden"
                onChange={handleFileUpload}
              />
            </div>
            <textarea
              id="content"
              value={content}
              onChange={e => handleContentChange(e.target.value)}
              placeholder={`Paste your meeting transcript here…\n\nExample:\nJordan: Let's kick off with the roadmap review.\nTaylor: I've pulled the data — we shipped 8 of 12 items from last cycle.`}
              rows={16}
              className="w-full border border-[var(--rule)] px-3 py-2.5 text-sm text-[var(--text-pri)] placeholder-gray-400 font-mono resize-y min-h-40 focus:outline-none focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 transition-colors"
            />
            <div className="flex items-center justify-between mt-1">
              <span className="text-xs text-[var(--text-ter)]">
                {content.length > 0 ? `${content.split(/\s+/).filter(Boolean).length.toLocaleString()} words` : ''}
              </span>
              {content.length > 0 && content.length < 100 && (
                <span className="text-xs text-[var(--mustard)]">
                  Transcript seems short — analysis works best with full meeting content
                </span>
              )}
            </div>
          </div>

          {/* Detected speakers */}
          {detectedSpeakers.length > 0 && (
            <div className="bg-[var(--green-dim)] border border-[var(--green)] px-4 py-3">
              <p className="text-xs font-medium text-[var(--green)] dark:text-[var(--text-ter)] mb-1.5">
                {detectedSpeakers.length} speaker{detectedSpeakers.length !== 1 ? 's' : ''} detected
              </p>
              <div className="flex flex-wrap gap-1.5">
                {detectedSpeakers.map(s => (
                  <span key={s} className="text-xs bg-[var(--bg-raised)] border border-[var(--green)] text-[var(--green)] dark:text-[var(--text-ter)] px-2 py-0.5 rounded-full">
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}

          {content.trim().length > 50 && detectedSpeakers.length === 0 && (
            <div className="bg-[var(--mustard-dim)] border border-[var(--mustard)] px-4 py-3 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-[var(--mustard)] shrink-0 mt-0.5" />
              <p className="text-xs text-[var(--mustard)]">
                No speakers detected. Make sure each turn starts with a speaker name (e.g. "Jordan:").
                See the format guide below.
              </p>
            </div>
          )}

          {/* Format guide */}
          <FormatGuide />

          {/* Status messages */}
          {stage === 'analyzing' && (
            <div className="bg-[var(--slate-dim)] border border-[var(--slate)] px-4 py-4 flex items-center gap-3">
              <Loader2 className="w-5 h-5 text-[var(--text-ter)] animate-spin shrink-0" />
              <div>
                <p className="text-sm font-medium text-[var(--text-sec)]">Analyzing transcript…</p>
                <p className="text-xs text-[var(--text-sec)] mt-0.5">
                  The LLM is scoring each speaker on NLP meta-model dimensions. This takes 15–45 seconds.
                </p>
              </div>
            </div>
          )}

          {stage === 'done' && (
            <div className="bg-[var(--green-dim)] border border-[var(--green)] px-4 py-4 flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-[var(--green)] shrink-0" />
              <div>
                <p className="text-sm font-medium text-[var(--green)] dark:text-[var(--text-ter)]">Analysis complete</p>
                <p className="text-xs text-[var(--green)] dark:text-[var(--text-ter)] mt-0.5">Redirecting to speaker profiles…</p>
              </div>
            </div>
          )}

          {stage === 'error' && errorMsg && (
            <div className="bg-[var(--coral-dim)] border border-[var(--coral)] px-4 py-3 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-[var(--coral)] shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-[var(--coral)]">Analysis failed</p>
                <p className="text-xs text-[var(--coral)] mt-0.5">{errorMsg}</p>
              </div>
            </div>
          )}

          {!lexiconAiEnabled && (
            <p className="text-sm text-[var(--mustard)] bg-[var(--mustard-dim)] border border-[var(--mustard)] px-3 py-2">
              Lexicon analysis is disabled for this workspace. Enable it in Settings → AI Assists.
            </p>
          )}

          {/* Submit */}
          <div className="flex items-center gap-3 pt-2">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className={`inline-flex items-center gap-2 px-5 py-2.5  text-sm font-medium transition-all
                ${canSubmit
                  ? 'bg-[var(--slate)] hover:bg-[var(--slate)] text-white'
                  : 'bg-[var(--hover-tint)] text-[var(--text-ter)] cursor-not-allowed'
                }`}
            >
              {stage === 'analyzing' ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing…</>
              ) : (
                <><Wand2 className="w-4 h-4" /> Analyze transcript</>
              )}
            </button>
            <button
              type="button"
              onClick={() => navigate('/prioritization/lexicon')}
              className="text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
